import { FC, useCallback, useEffect, useState } from 'react';
import { LandscapeContentBlocksTemplate } from '../../templates';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import { useNavigate, useParams } from 'react-router-dom';
import {
  deleteBlock,
  fetchBlocks,
} from '../../../store/landscapeAPI/block/reducers/block.slice';
import { ImageBlockResponseDto } from '../../../store/landscapeAPI/block/dto/response/image-block-response.dto';
import { BlockType } from '../../../store/landscapeAPI/block/dto/block-type';
import { RichTextBlockResponseDto } from '../../../store/landscapeAPI/block/dto/response/rich-text-block-response.dto';
import cn from 'classnames';
import styles from './LandscapeContentBlocksPage.module.css';
import { BlockItem } from '../../atoms/SortableImageBlockItem/SortableImageBlockItem';
import Spinner from '../../atoms/Spinner/Spinner';
import { EntityBlockMappingResponseDto } from '../../../store/landscapeAPI/block/dto/response/entity-block-mapping-response.dto';
import { LocalesLanguagesConstants } from '../../../locales/locales';

interface LandscapeContentBlocksPageProps {}

const LandscapeContentBlocksPage: FC<LandscapeContentBlocksPageProps> = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { id, storyId } = useParams();
  const navigate = useNavigate();
  const { status } = useSelector((state: RootState) => state.blockSlice);

  const [data, setData] = useState<BlockItem[]>();
  const [changeOrderEnabled, setChangeOrderEnabled] = useState(false);

  const processBlockData = useCallback(
    (resp: EntityBlockMappingResponseDto[]) => {
      const blocks = resp
        .map((block: EntityBlockMappingResponseDto) => {
          if (block && block.block) {
            let imageUrl: string | null = null;
            let backgroundImageUrl: string | null = null;
            let backgroundColor: string | null = null;
            let content: string | null = null;
            let title: string = '';
            let type = BlockType.ImageBlock;

            if (block.block.blockType === BlockType.ImageBlock) {
              const imageBlock = block.block as ImageBlockResponseDto;
              imageUrl = imageBlock.imageUrl;
              backgroundImageUrl = imageBlock.backgroundImageUrl;
              backgroundColor = imageBlock.backgroundColor;
              title = BlockType.ImageBlock;
            }

            if (block.block.blockType === BlockType.RichTextBlock) {
              const richTextBlock = block.block as RichTextBlockResponseDto;
              content = richTextBlock.content;
              title = BlockType.RichTextBlock;
              type = BlockType.RichTextBlock;
            }

            return {
              blockId: block.block.id,
              id: block.id,
              sortOrder: block.sortOrder,
              imageUrl,
              backgroundImageUrl,
              backgroundColor,
              content,
              title,
              type,
            } as BlockItem;
          }
          return null;
        })
        .filter(Boolean);

      setData(blocks as BlockItem[]);
    },
    []
  );

  useEffect(() => {
    dispatch(
      fetchBlocks({
        id: storyId!,
        type: 'compactstory',
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then(processBlockData);
  }, [dispatch, storyId, processBlockData]);

  const onReorder = (items: BlockItem[], from: number, to: number) => {
    setData(items);
  };

  const handleEdit = async (blockId: string, type: BlockType) => {
    const blockType = type === BlockType.ImageBlock ? 'image' : 'text';
    const path = `/landscapes/edit/${id}/updates/story/edit/${storyId}/${blockType}-block/edit/${blockId}`;
    navigate(path);
  };

  const handleDelete = async (blockId: string) => {
    await dispatch(deleteBlock({ existingId: blockId, compactId: id! }));
    dispatch(
      fetchBlocks({
        id: storyId!,
        type: 'compactstory',
        language: LocalesLanguagesConstants.en,
      })
    )
      .unwrap()
      .then(processBlockData);
  };

  if (status === 'loading') {
    return <Spinner />;
  }

  return (
    <div className={cn(styles.wrapper)}>
      <LandscapeContentBlocksTemplate
        blockItems={data ?? []}
        setBlockItems={onReorder}
        changeOrderEnabled={changeOrderEnabled}
        setChangeOrderEnabled={setChangeOrderEnabled}
        handleDelete={handleDelete}
        handleEdit={handleEdit}
      />
    </div>
  );
};

export default LandscapeContentBlocksPage;
